<template>
    <div class="layout-no-topbar" :class="containerClass" :data-theme="state.colorScheme"
        @click="onDocumentClick($event)">
        <button ref="menuToggleButton" type="button" class="menu-toggle-button" aria-controls="app-sidebar"
            :aria-expanded="menuExpanded" :aria-label="menuExpanded ? 'Recolher menu' : 'Expandir menu'"
            @click="onMenuToggleClick">
            <i :class="menuToggleIcon" aria-hidden="true"></i>
        </button>

        <AppMenu :layoutMode="state.layoutMode" :menuTheme="menuTheme" :currentUser="currentUser"
            :sidebarStatic="state.sidebarStatic" :menuActive="state.menuActive"
            :mobileMenuActive="state.staticMenuMobileActive" @menu-click="onMenuClick"
            @menuitem-click="onMenuItemClick" @root-menuitem-click="onRootMenuItemClick"
            @my-account="openMyAccount" @logout="logout" />

        <div class="layout-content-wrapper" :inert="state.staticMenuMobileActive ? '' : null">
            <div class="layout-main">
                <div class="layout-content">
                    <router-view />
                </div>
                <AppFooter />
            </div>
        </div>

        <div class="layout-mask modal-in"></div>

        <NotificationToast />
    </div>
</template>

<script>
import { DomHandler } from 'primevue/utils'
import EventBus from './event-bus'
import AppMenu from "./AppMenu"
import AppFooter from "./AppFooter"
import NotificationToast from "@/components/NotificationToast"
import { reactive, computed, watch, shallowRef, nextTick, getCurrentInstance, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import useNotificationToast from '../src/composables/useNotificationToast'
import { useStore } from 'vuex'
export default {

    props: {
        menuTheme: String
    },

    components: {
        AppMenu,
        AppFooter,
        NotificationToast
    },

    setup(props) {
        const state = reactive({
            menuActive: false,
            layoutMode: "sidebar",
            colorScheme: "light",
            sidebarStatic: true,
            staticMenuMobileActive: false,
            isMobileViewport: isMobile(),
            menuClick: false
        })

        const vm = getCurrentInstance()

        const containerClass = computed(() => {
            return [
                "layout-wrapper",
                {
                    "layout-sidebar": state.layoutMode === "sidebar",
                    "layout-static": state.layoutMode === "sidebar" && state.sidebarStatic,
                    "layout-horizontal": state.layoutMode === "horizontal",
                    "layout-slim": state.layoutMode === "slim",
                    'layout-mobile-active': state.staticMenuMobileActive,
                    "p-input-filled": vm.appContext.config.globalProperties.$primevue.config.inputStyle === "filled",
                    "p-ripple-disabled": !vm.appContext.config.globalProperties.$primevue.config.ripple,
                },
                "layout-menu-" + props.menuTheme
            ]
        })

        const store = useStore()
        const route = useRoute()
        const router = useRouter()
        const menuToggleButton = shallowRef(null)
        const { toast, success, error, info, warn } = useNotificationToast()
        const currentUser = computed(() => store.getters.getUser || {})

        // no desktop o botão expande/recolhe a sidebar; no mobile abre/fecha a gaveta
        const menuExpanded = computed(() => {
            return state.isMobileViewport ? state.staticMenuMobileActive : state.sidebarStatic
        })

        const menuToggleIcon = computed(() => {
            if (state.isMobileViewport) {
                return state.staticMenuMobileActive ? 'pi pi-times' : 'pi pi-bars'
            }

            return state.sidebarStatic ? 'pi pi-angle-left' : 'pi pi-angle-right'
        })

        watch(route, async () => {
            state.menuActive = false
            toast.removeAllGroups()
        })

        onMounted(() => {
            document.addEventListener("visibilitychange", onVisibilityChange)
            window.addEventListener("focus", redirectIfTokenExpired)
            document.addEventListener('sendToast', onSendToast)
            document.addEventListener('keydown', onDocumentKeydown)
            window.addEventListener('resize', onWindowResize)
        })

        onBeforeUnmount(() => {
            document.removeEventListener("visibilitychange", onVisibilityChange)
            window.removeEventListener("focus", redirectIfTokenExpired)
            document.removeEventListener('sendToast', onSendToast)
            document.removeEventListener('keydown', onDocumentKeydown)
            window.removeEventListener('resize', onWindowResize)
            unblockBodyScroll()
        })

        function onWindowResize() {
            state.isMobileViewport = isMobile()

            // ao voltar para o desktop a gaveta mobile não faz mais sentido
            if (!state.isMobileViewport && state.staticMenuMobileActive) {
                hideOverlayMenu()
            }
        }

        function onVisibilityChange() {
            if (document.visibilityState == "visible") {
                redirectIfTokenExpired()
            }
        }

        function onSendToast(data) {
            switch (data.detail.type) {
                case 'success': success(data.detail.message); break
                case 'error': error(data.detail.message); break
                case 'info': info(data.detail.message); break
                case 'warn': warn(data.detail.message); break
                default: info(data.detail.message); break
            }
        }

        function redirectIfTokenExpired() {

            if (new Date(store.state.expires_at) <= new Date) {
                hideOverlayMenu()
                store.dispatch('setLogged', {
                    isLogged: false
                })
                store.dispatch('setUser', {
                    user: null
                })

                router.push({ name: 'Login', query: { 'expired': true } })
            }

        }

        function logout() {
            hideOverlayMenu()
            store.dispatch('setLogged', {
                isLogged: false
            })
            store.dispatch('setUser', {
                user: null
            })

            router.push({ name: 'Login' })
        }

        function openMyAccount() {
            hideOverlayMenu()

            router.push({ name: 'MyAccount' })
        }

        function onDocumentClick() {
            if (!state.menuClick) {
                if (isSlim() || isHorizontal()) {
                    EventBus.emit('reset-active-index')
                    state.menuActive = false
                }

                if (state.staticMenuMobileActive) {
                    hideOverlayMenu()
                }

                unblockBodyScroll()
            }

            state.menuClick = false
        }

        function onMenuClick(event) {
            if (isHorizontal() && DomHandler.hasClass(event.target, 'layout-menu-container')) {
                EventBus.emit('reset-active-index')
                state.menuClick = false
                state.menuActive = false
            }
            state.menuClick = true
        }

        function onMenuToggleClick(event) {
            state.menuClick = true

            if (state.isMobileViewport) {
                state.staticMenuMobileActive = !state.staticMenuMobileActive

                if (state.staticMenuMobileActive) {
                    blockBodyScroll()
                } else {
                    unblockBodyScroll()
                }
            } else {
                state.sidebarStatic = !state.sidebarStatic
            }

            event.preventDefault()
        }

        // no document, e não no container: o Esc precisa funcionar mesmo sem foco dentro do app
        function onDocumentKeydown(event) {
            if (event.key !== 'Escape' || !state.staticMenuMobileActive) return

            hideOverlayMenu()
            nextTick(() => menuToggleButton.value?.focus())
        }

        function onMenuItemClick(event) {
            if (!event.item.items) {
                EventBus.emit('reset-active-index')
                hideOverlayMenu()
            }
            if (!event.item.items && isSlim()) {
                state.menuActive = false
            }
        }

        function onRootMenuItemClick() {
            state.menuActive = !state.menuActive
        }

        function hideOverlayMenu() {
            state.staticMenuMobileActive = false
            unblockBodyScroll()
        }

        function blockBodyScroll() {
            if (document.body.classList) {
                document.body.classList.add('blocked-scroll')
            } else {
                document.body.className += ' blocked-scroll'
            }
        }

        function unblockBodyScroll() {
            if (document.body.classList) {
                document.body.classList.remove('blocked-scroll')
            } else {
                document.body.className = document.body.className.replace(new RegExp('(^|\\b)' +
                    'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'), ' ')
            }
        }

        function isSlim() {
            return state.layoutMode === "slim"
        }

        function isHorizontal() {
            return state.layoutMode === "horizontal"
        }

        function isMobile() {
            return window.innerWidth <= 991
        }

        return {
            state,
            containerClass,
            currentUser,
            menuToggleButton,
            menuExpanded,
            menuToggleIcon,
            redirectIfTokenExpired,
            logout,
            openMyAccount,
            onDocumentClick,
            onMenuClick,
            onMenuToggleClick,
            onMenuItemClick,
            onRootMenuItemClick,
            hideOverlayMenu,
            blockBodyScroll,
            unblockBodyScroll
        }
    }
}
</script>
